/* One function per backend call. The shapes for the NEW endpoints follow
 * docs/agency-app-contract.md; the shapes for the existing ones are read from
 * the Go handlers named beside each, so a change there is found here.
 *
 * Money is always a decimal STRING from the server. The app never does money
 * arithmetic of its own. */

import { api, fetchFile } from './api';

/* ------------------------------------------------------------------ profile */

export type AgencyStatus = 'PENDING_REVIEW' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED' | string;

export interface Profile {
  agency: {
    public_id: string;
    display_name: string;
    legal_name: string;
    status: AgencyStatus;
    rejection_reason: string | null;
    city_code: string;
    city_name: string;
    contact_phone_e164: string;
    contact_email: string;
    /** The registered office address fields (editable on Profile). Absent from
     *  an older server that does not yet return them. */
    registered_landmark?: string;
    registered_pincode?: string;
  };
  me: { full_name: string; role: string };
  /** overdue_to_staff (2026-10-23): the part of to_staff past its staffing deadline — an open
   *  unstaffed escalation (000079). Absent from an older server. */
  counts: {
    open_offers: number;
    to_staff: number;
    overdue_to_staff?: number;
    live_jobs: number;
    join_requests: number;
    /** 000097: people still checked in past their shift's end plus the grace. Absent from an older server. */
    checkout_overdue?: number;
  };
}

export const getProfile = () => api<Profile>('/agency/profile');

/** What the owner or an admin may change about the agency and themselves.
 *  OWNER/ADMIN only — the server refuses the rest (hiding the form is not a
 *  permission). PUT /agency/profile. */
export interface ProfileEdit {
  display_name: string;
  contact_phone_e164: string;
  contact_email: string;
  registered_landmark: string;
  registered_pincode: string;
  full_name: string;
}

export const updateProfile = (body: ProfileEdit) =>
  api<{ ok: boolean }>('/agency/profile', { method: 'PUT', body: JSON.stringify(body) });

/** What the platform told the signed-in member on this app (GET
 *  /me/notifications): newest first, 30 days, at most 50. A record, not state. */
export interface Notice {
  public_id: string;
  kind: string;
  urgency: 'URGENT' | 'ROUTINE' | string;
  title: string;
  body: string;
  created_at: string;
}
export const listNotices = () => api<Notice[]>('/me/notifications');

/* ------------------------------------------------------------------ offers
 * dispatch_handlers.go / dispatch_views.go offerView. An offer carries what is
 * needed to price the job and NOTHING that identifies the patient. */

export interface OfferItem {
  public_id: string;
  service_code: string;
  variant_code: string | null;
  quantity: number;
  is_immediate: boolean;
  scheduled_start_utc: string | null;
  scheduled_end_utc: string | null;
  service_pincode: string | null;
  service_city: string | null;
  attendant_help?: string;
  physio_note?: string;
  doctor_note?: string;
  nurse?: {
    start_date: string;
    end_date: string;
    shift_pattern: string;
    shifts_per_day: number;
    nurses_required: number;
    gender_preference: string;
    care_task_codes: string[];
  };
  [detail: string]: unknown;
}

export interface Offer {
  public_id: string;
  status: string; // OFFERED | QUOTED | ACCEPTED | DECLINED | EXPIRED | ...
  broadcast_round: number;
  radius_km: number | null;
  distance_km: number | null;
  offered_at: string;
  expires_at: string;
  quote_expires_at_max: string | null;
  pricing_mode: 'CEILING' | 'QUOTE' | string;
  ceiling_amount: string | null;
  /** The family's total on a platform-priced job — what this agency takes the
   *  job at if it accepts (owner rulings 2026-10-22/23). null without one. */
  platform_price: string | null;
  /** Accept will take this offer on price grounds: a fixed-price job (ambulance
   *  ceiling, or any platform-priced job). false: no platform price yet. */
  can_accept: boolean;
  quoted_amount: string | null;
  quote_expires_at: string | null;
  quote_note: string | null;
  decline_reason: string | null;
  responded_at: string | null;
  won_by: string | null;
  item: OfferItem;
}

export const listOffers = () => api<Offer[]>('/agency/offers');
export const listOfferHistory = () => api<Offer[]>('/agency/offers?status=history&limit=50');
export const acceptOffer = (id: string) => api<Offer>(`/agency/offers/${encodeURIComponent(id)}/accept`, { method: 'POST' });
/* NO QUOTES (owner rulings 2026-10-22/23). Every job a family books has one
 * price, the platform's, and the first provider to accept takes it at that
 * price — so the desk never sends a quote, and the quote call is gone from it.
 *
 * offerPrice is the number an offer pays: the platform price where there is
 * one, else an ambulance's ceiling. null: a job with no platform price yet,
 * which cannot be accepted (the server answers PLATFORM_PRICE_MISSING). An
 * older server sends no can_accept; then only a non-QUOTE job is acceptable. */
export function offerPrice(o: Offer): string | null {
  return o.platform_price ?? (o.pricing_mode !== 'QUOTE' ? o.ceiling_amount : null);
}
export function offerAcceptable(o: Offer): boolean {
  return typeof o.can_accept === 'boolean' ? o.can_accept : o.pricing_mode !== 'QUOTE';
}
export const declineOffer = (id: string, decline_reason: string) =>
  api<Offer>(`/agency/offers/${encodeURIComponent(id)}/decline`, {
    method: 'POST',
    body: JSON.stringify({ decline_reason }),
  });

/* ------------------------------------------------------------------ items
 * binding_handlers.go ListAgencyItems / GetAgencyItem, binding_views.go
 * postWinView. After a win the agency sees the address and the patient's first
 * name: it is now sending someone to the door. */

export interface Coverage {
  assigned_until: string | null;
  next_uncovered_shift_at: string | null;
}

export interface ItemSummary {
  public_id: string;
  service_code: string;
  variant_code?: string | null;
  status: string; // AGENCY_ACCEPTED | ASSIGNED | IN_PROGRESS
  locality: { pincode: string; city_code: string };
  start_date: string | null;
  end_date: string | null;
  coverage: Coverage;
  /** 000097: people on this job still checked in past their shift's end plus the grace. */
  checkout_overdue?: number;
}

export interface Shift {
  public_id: string;
  sequence_no: number;
  shift_date: string;
  shift_type: string;
  start_utc: string;
  end_utc: string;
  status: string;
  checked_in_at_utc: string | null;
  checked_out_at_utc: string | null;
}

export interface Attendance {
  at_utc: string;
  method: string;
  within_geofence: boolean | null;
  override_reason: string | null;
  recorded_by_agency: boolean;
}

export interface Assignment {
  public_id: string;
  status: string; // PROPOSED | ACCEPTED | ACTIVE | COMPLETED | REJECTED | WITHDRAWN | ...
  resource_type: 'STAFF' | 'VEHICLE' | 'EQUIPMENT_UNIT' | string;
  role: string;
  resource_public_id: string;
  resource_label: string;
  shift_public_id: string | null;
  assigned_by_type: string;
  assigned_at: string;
  responded_at: string | null;
  reject_reason: string | null;
  unmet_criteria: string[];
  started_at_utc: string | null;
  completed_at_utc: string | null;
  check_in?: Attendance;
  check_out?: Attendance;
}

export interface ItemDetail {
  public_id: string;
  booking_public_id: string;
  service_code: string;
  variant_code?: string | null;
  status: string;
  service_address: { text: string | null; landmark: string | null; pincode: string; lat: number | null; lng: number | null };
  contact: { name: string; phone: string };
  patient: { first_name: string; sex: string; approximate_age: number | null };
  detail: Record<string, unknown> | null;
  shifts: Shift[];
  assignments: Assignment[];
  coverage: Coverage;
  price: { total_amount?: string; [k: string]: unknown } | null;
  nurse_preference: { resource_public_id: string; label: string; set_at_utc: string; outcome: string } | null;
}

export interface Candidate {
  resource_public_id: string;
  resource_type: string;
  label: string;
  eligible: boolean;
  refusals: { code: string; reason: string }[];
  unmet: string[];
  preferred: boolean;
  /** A person's details (2026-10-27); null for an ambulance or unit. Only the
   *  right kind of worker for the role is listed at all. Absent on an older server. */
  staff?: { staff_type: string; phone: string | null; on_duty: boolean; qualification: string | null } | null;
  /** An ambulance's details (2026-10-27); null for a person or unit. */
  vehicle?: { variant_code: string | null; make: string | null; model: string | null; has_oxygen: boolean; has_ventilator: boolean } | null;
}

export interface BindAssignment {
  shift?: string;
  role: string;
  resource_type: string;
  resource: string;
}

export const listItems = () => api<ItemSummary[]>('/agency/items');
export const getItem = (id: string) => api<ItemDetail>(`/agency/items/${encodeURIComponent(id)}`);
export const listCandidates = (id: string, role: string, shift?: string) => {
  const q = new URLSearchParams({ role });
  if (shift) q.set('shift', shift);
  return api<Candidate[]>(`/agency/items/${encodeURIComponent(id)}/candidates?${q.toString()}`);
};
export const bindItem = (id: string, assignments: BindAssignment[], acknowledged_unmet: string[], replaces?: string) =>
  api<ItemDetail>(`/agency/items/${encodeURIComponent(id)}/bind`, {
    method: 'POST',
    body: JSON.stringify({ assignments, acknowledged_unmet, ...(replaces ? { replaces } : {}) }),
  });
/** Check a nurse out on her behalf (visit-phase §6.4; 000097 put it on a screen): she
 *  forgot, her phone died, she is not coming back to press the button. The time
 *  she LEFT (between her check-in and now) and why; recorded as the agency's act. */
export const checkOutOnBehalf = (id: string, occurred_at_utc: string, override_reason: string) =>
  api<unknown>(`/agency/assignments/${encodeURIComponent(id)}/check-out`, {
    method: 'POST',
    body: JSON.stringify({ occurred_at_utc, override_reason }),
  });
/** Give back a job this agency won but cannot do (2026-10-30): its people are released and the
 *  job is offered again at once to every other agency and independent worker near the family;
 *  this agency is not asked again. Refused once work has started (ITEM_IN_PROGRESS). */
export const giveBackJob = (id: string, reason: string) =>
  api<unknown>(`/agency/items/${encodeURIComponent(id)}/give-back`, { method: 'POST', body: JSON.stringify({ reason }) });
export const withdrawAssignment = (id: string) =>
  api<{ assignment_public_id: string; item_status: string }>(`/agency/assignments/${encodeURIComponent(id)}/withdraw`, {
    method: 'POST',
  });

/* ------------------------------------------------------------------ team (contract) */

export interface StaffMember {
  public_id: string;
  full_name: string;
  phone_e164: string;
  staff_type: string;
  staff_code: string;
  status: string;
  employee_ref: string | null;
  confirmed: boolean;
  joined_on: string | null;
  on_duty: boolean;
  rejection_reason: string | null;
}

export interface Vehicle {
  public_id: string;
  registration_number: string;
  make: string | null;
  model: string | null;
  manufacture_year: number | null;
  variant_code: string | null;
  status: string; // ACTIVE | MAINTENANCE | RETIRED
  stretcher_capacity: number | null;
  seating_capacity: number | null;
  has_oxygen: boolean;
  has_ventilator: boolean;
  has_defibrillator: boolean;
  has_suction: boolean;
  /** The driver who brought it, or null for the agency's own fleet. */
  owner_name: string | null;
  /** The trip it is on now, if any. */
  live_trip_status: string | null;
  live_trip_item_public_id: string | null;
  live_trip_driver: string | null;
  trips_completed: number;
  last_trip_at: string | null;
}

/** Take an ambulance off the road for repair, or put it back (OWNER / ADMIN).
 *  Refused VEHICLE_ON_A_JOB while it is on a trip. */
export const setVehicleStatus = (id: string, status: 'ACTIVE' | 'MAINTENANCE', reason: string) =>
  api<{ public_id: string; status: string }>(`/agency/vehicles/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, reason }),
  });

/** An ambulance the agency adds to its own fleet (POST /agency/vehicles, 2026-10-30, OWNER / ADMIN).
 *  It takes trips once its papers are approved — upload them on Documents. */
export interface NewAmbulance {
  registration_number: string;
  make: string;
  model: string;
  manufacture_year: number;
  has_oxygen: boolean;
  has_ventilator: boolean;
  has_defibrillator: boolean;
  has_suction: boolean;
}
export const addAmbulance = (b: NewAmbulance) =>
  api<{ public_id: string; status: string }>("/agency/vehicles", { method: "POST", body: JSON.stringify(b) });

export interface Team {
  staff: StaffMember[];
  vehicles: Vehicle[];
}

export const getTeam = () => api<Team>('/agency/team');
export const confirmStaff = (id: string) =>
  api<{ public_id: string; confirmed: boolean }>(`/agency/team/${encodeURIComponent(id)}/confirm`, { method: 'POST' });
export const declineStaff = (id: string, reason: string) =>
  api<unknown>(`/agency/team/${encodeURIComponent(id)}/decline`, { method: 'POST', body: JSON.stringify({ reason }) });

/** A nurse's skills (owner, 2026-10-31): she ticks them when she signs up in the
 *  partner app, and the agency adds or removes them here. Every care task, each
 *  marked held (`granted`) and whether an agency user signed it off
 *  (`signed_off`) rather than her own pick at sign-up. Nurses and attendants
 *  only — a driver or doctor is 409 INVALID_STAFF_TYPE. */
export interface StaffSkill {
  code: string;
  name: string;
  description: string;
  category: string;
  granted: boolean;
  signed_off: boolean;
}
export interface StaffSkillSheet {
  /** Skills in the worker's own words (000103). They match no job. */
  custom?: { public_id: string; name: string }[];
  public_id: string;
  staff_type: string;
  skills: StaffSkill[];
}
export const getStaffSkills = (id: string) => api<StaffSkillSheet>(`/agency/staff/${encodeURIComponent(id)}/skills`);
/** The whole set (OWNER / ADMIN): every code listed is held, every other is removed. */
export const setStaffSkills = (id: string, codes: string[]) =>
  api<StaffSkillSheet>(`/agency/staff/${encodeURIComponent(id)}/skills`, { method: 'PUT', body: JSON.stringify({ codes }) });

/** Onboarding invites (000081): the agency onboards a worker and gets a link
 *  to send them; the worker signs up through it, auto-attached to this agency. */
export interface StaffInvite {
  public_id: string;
  phone: string;
  full_name: string;
  staff_type: string;
  agency_employee_ref: string;
  status: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  token?: string;
  accept_url?: string;
}
export const listInvites = () => api<StaffInvite[]>('/agency/invites');
export const createInvite = (b: { phone: string; full_name: string; staff_type: string; agency_employee_ref: string }) =>
  api<StaffInvite>('/agency/invites', { method: 'POST', body: JSON.stringify(b) });
export const revokeInvite = (id: string) =>
  api<unknown>(`/agency/invites/${encodeURIComponent(id)}/revoke`, { method: 'POST' });

/** departure_handlers.go: left_on is the last day employed (YYYY-MM-DD);
 *  omitted means immediately. */
export const recordDeparture = (id: string, reason: string, left_on?: string) =>
  api<unknown>(`/agency/staff/${encodeURIComponent(id)}/departure`, {
    method: 'POST',
    body: JSON.stringify(left_on ? { reason, left_on } : { reason }),
  });
export const withdrawDeparture = (id: string) =>
  api<unknown>(`/agency/staff/${encodeURIComponent(id)}/departure`, { method: 'DELETE' });

/* ------------------------------------------------------------------ coverage (contract) */

export interface CoverageArea {
  public_id?: string;
  label: string;
  city_code?: string;
  lat: number;
  lng: number;
  radius_km: number;
}

export interface CoverageSetup {
  services: { code: string; name: string; active: boolean }[];
  areas: CoverageArea[];
}

export const getCoverage = () => api<CoverageSetup>('/agency/coverage');
export const saveCoverage = (services: string[], areas: { label: string; lat: number; lng: number; radius_km: number }[]) =>
  api<CoverageSetup>('/agency/coverage', { method: 'PUT', body: JSON.stringify({ services, areas }) });

/* ------------------------------------------------------------------ documents
 * submission_handlers.go readSubmission: multipart with these form fields and
 * one part named "file" (PDF, JPEG or PNG; sniffed by the server). */

export interface DocumentFields {
  document_type_code: string;
  document_number?: string;
  masked_value?: string;
  issuing_authority?: string;
  issued_on?: string;
  expires_on?: string;
}

export interface Submitted {
  public_id: string;
  [k: string]: unknown;
}

function documentForm(fields: DocumentFields, file: File, agencyPublicId?: string): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (typeof v === 'string' && v.trim() !== '') form.append(k, v.trim());
  }
  if (agencyPublicId) form.append('agency_public_id', agencyPublicId);
  form.append('file', file);
  return form;
}

export const submitAgencyDocument = (agencyPublicId: string, fields: DocumentFields, file: File) =>
  api<Submitted>('/agency/documents', { method: 'POST', body: documentForm(fields, file, agencyPublicId) });
export const submitVehicleDocument = (vehiclePublicId: string, fields: DocumentFields, file: File) =>
  api<Submitted>(`/agency/vehicles/${encodeURIComponent(vehiclePublicId)}/documents`, {
    method: 'POST',
    body: documentForm(fields, file),
  });

/* ------------------------------------------------------------------ earnings (contract) */

/** GET /agency/earnings. Since 000077 the platform collects every payment and
 *  pays the agency its earnings LESS the cash its people collected from
 *  families on its jobs: `owed` is what the next payment will be (accrued less
 *  the held cash it can absorb), `cash_beyond_earnings` the held cash it
 *  cannot. Every figure is the server's. */
export interface Earnings {
  summary: { accrued: string; paid: string; this_month: string; cash_held: string; owed: string; cash_beyond_earnings: string };
  lines: {
    public_id: string;
    booking_public_id: string;
    item_public_id: string;
    service_code: string;
    staff_name: string;
    amount: string;
    status: string;
    earned_at: string;
  }[];
  payouts: {
    public_id: string;
    amount: string;
    earnings_amount: string;
    cash_netted_amount: string;
    status: string;
    paid_at: string | null;
    reference: string; // "" when none (a payout netted to zero moved no money)
    lines: number;
    cash_collections: number;
  }[];
  /** Cash your people collected on your jobs. settlement: null while you hold
   *  it, AGENCY_NETTED once a payment took it off (payout_public_id names it),
   *  DEPOSITED when it was handed in to the platform, PAYOUT_NETTED when it
   *  was taken off the collector's own pay. status is DECLARED | CONFIRMED |
   *  DISPUTED (binding/agency_earnings.go). */
  cash: {
    public_id: string;
    booking_public_id: string;
    staff_name: string;
    amount: string;
    status: string;
    settlement: string | null;
    payout_public_id: string | null;
    collected_at: string;
  }[];
}

export const getEarnings = () => api<Earnings>('/agency/earnings');

/* ------------------------------------------------------------------ compliance
 * GET /agency/compliance — onboarding.AgencyCompliance: the admin's own read of
 * this agency (checklist, papers, per-service eligibility after a recompute),
 * plus each vehicle's papers and the document types it may upload. */

export interface DocState {
  state: string; // CURRENT (in force) | MISSING | REJECTED | EXPIRED — onboarding/readiness_view.go
  public_id?: string;
  expires_on?: string;
  rejection_reason?: string;
}

export interface ChecklistItem {
  document_type_code: string;
  service_code: string | null;
  trigger_condition: string;
  trigger_met: boolean;
  is_mandatory: boolean;
  blocks_dispatch: boolean;
  document: DocState;
  pending_renewal?: DocState;
}

export interface DocumentRow {
  public_id: string;
  document_type_code: string;
  document_number: string | null;
  masked_value: string | null;
  issuing_authority: string | null;
  issued_on: string | null;
  expires_on: string | null;
  status: string;
  rejection_reason: string | null;
  reviewed_at: string | null;
  submitted_at: string;
  file?: { sha256: string; mime_type: string; size_bytes: number } | null;
}

export interface EligibilityRow {
  service_code: string;
  city_code: string;
  equipment_type_code: string | null;
  is_dispatchable: boolean;
  blocked_reason: string | null;
  blocking_document_type_code: string | null;
  computed_reason_detail: string | null;
  computed_at: string;
}

export interface DocumentType {
  code: string;
  name: string;
  subject_type: 'AGENCY' | 'VEHICLE' | string;
  description: string | null;
  has_expiry: boolean;
  is_identity_document: boolean;
  blocks_dispatch: boolean;
}

export interface Compliance {
  agency: { public_id: string; status: string; rejection_reason: string | null };
  services_offered: string[];
  documents: DocumentRow[];
  checklist: ChecklistItem[];
  eligibility: EligibilityRow[];
  as_of: string;
  vehicles: { public_id: string; registration_number: string; status: string; documents: DocumentRow[] }[];
  document_types: DocumentType[];
}

export const getCompliance = () => api<Compliance>('/agency/compliance');

/* ------------------------------------------------------------------ overview */

/** GET /agency/overview: the last seven days' requests and what became of
 *  them, and the accepted work from now to the end of tomorrow (IST) with who
 *  is going. Never the patient. */
export interface OverviewWeek {
  received: number;
  accepted: number;
  declined: number;
  missed: number;
  taken_by_others: number;
  cancelled: number;
  open: number;
  completed: number;
}
export interface UpcomingPerson {
  name: string;
  role: string;
  accepted: boolean;
}
export interface UpcomingJob {
  item_public_id: string;
  shift_public_id: string | null;
  service_code: string;
  item_status: string;
  start_utc: string | null;
  end_utc: string | null;
  is_immediate: boolean;
  consult_mode: string | null;
  pincode: string;
  vehicle: string | null;
  people: UpcomingPerson[];
}
export interface LivePerson {
  name: string;
  role: string;
  status: 'ACTIVE' | 'ACCEPTED' | 'PROPOSED';
  started_at: string | null;
}
/** A won job with someone on it (ASSIGNED / IN_PROGRESS), under way first:
 *  who is at work now, and who is booked for the next shift (or a trip's crew). */
export interface LiveJob {
  item_public_id: string;
  service_code: string;
  item_status: string;
  pincode: string;
  consult_mode: string | null;
  vehicle: string | null;
  next_start_utc: string | null;
  at_work: LivePerson[];
  next: LivePerson[];
}
export interface Overview {
  week: OverviewWeek;
  week_since: string;
  upcoming: UpcomingJob[];
  live: LiveJob[];
  upcoming_end: string;
  as_of: string;
}
export const getOverview = () => api<Overview>('/agency/overview');

/* ------------------------------------------------------------------ registration */

export interface City {
  code: string;
  name: string;
}

/** The cities an agency can register in. Public: the registering person has no
 *  agency session yet. */
export async function listCities(): Promise<City[]> {
  const res = await fetch('/agencies/cities');
  if (!res.ok) throw new Error('cities');
  const body = (await res.json()) as { data?: City[] } | City[];
  return Array.isArray(body) ? body : (body.data ?? []);
}

/* ------------------------------------------------------------------ prices (contract: "Prices")
 * The agency's own price for each type of each service it offers. A family is
 * shown the agency only for a type it has a price for; saving takes effect at
 * once (no platform approval). platform_price is what independent workers are
 * paid at, for reference. */

export interface AgencyPriceAmounts {
  base_amount: string;
  included_km: string | null;
  per_km_amount: string | null;
  updated_at: string;
}

export interface AgencyPriceItem {
  service_code: string;
  service_name: string;
  variant_code: string;
  variant_name: string;
  rate_basis: string;
  unit_label: string;
  price: AgencyPriceAmounts | null;
  platform_price: string | null;
  platform_included_km: string | null;
  platform_per_km_amount: string | null;
}

export interface AgencyPriceList {
  city_code: string;
  items: AgencyPriceItem[];
}

export interface AgencyPriceInput {
  service_code: string;
  variant_code: string;
  base_amount: string;
  included_km?: string;
  per_km_amount?: string;
}

export const getPrices = () => api<AgencyPriceList>('/agency/prices');
export const savePrice = (body: AgencyPriceInput) =>
  api<AgencyPriceItem>('/agency/prices', { method: 'PUT', body: JSON.stringify(body) });
export const clearPrice = (service: string, variant: string) =>
  api<AgencyPriceItem>(
    `/agency/prices?service_code=${encodeURIComponent(service)}&variant_code=${encodeURIComponent(variant)}`,
    { method: 'DELETE' },
  );

/* ------------------------------------------------------------------ the price sheet (2026-10-30)
 * Download your price list as Excel, fill in the prices, upload it: the
 * server checks every row and says what would change; Apply sets every new
 * and changed price at once — all or none. A blank price cell leaves that
 * price as it is. */

export interface PriceSheetRow {
  line: number;
  service_code: string;
  variant_code: string;
  variant_name?: string;
  base_amount?: string;
  included_km?: string;
  per_km_amount?: string;
  /** NEW | CHANGED | UNCHANGED | BLANK | ERROR */
  status: string;
  message?: string;
  current_base_amount: string | null;
  current_included_km: string | null;
  current_per_km_amount: string | null;
}

export interface PriceSheetCheck {
  rows: PriceSheetRow[];
  new: number;
  changed: number;
  unchanged: number;
  blank: number;
  errors: number;
  applied: number;
}

export const downloadPriceSheet = () => fetchFile('/agency/prices/sheet');
export const checkPriceSheet = (file: File) =>
  api<PriceSheetCheck>('/agency/prices/sheet/check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: file,
  });
export const applyPriceSheet = (file: File) =>
  api<PriceSheetCheck>('/agency/prices/sheet/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: file,
  });
